import { CharacterMetadata, Entity } from 'draft-js'
import { ANNOTATION_STYLE } from '../../DraftEditor/plugins/draft-js-annotations'
import { AnnotationData } from '../../DraftEditor/plugins/draft-js-annotations/types'
import { _stripCharacters } from './highlightColorMiddleware'

const TAG_REGEX = /<mark\W+(?:data-annotation-category="([^"]+)")\W(?:data-annotation-item="([^"]+)")\W(?:data-annotation-child="([^"]+)"|data-annotation-child="")>([\s\S]*?)<\/mark>/gim

const CATEGORY_BOUNDARY = '---ANN0T4T10N_C4T3G0R1---'
const ITEM_BOUNDARY = '---ANN0T4T10N_1T3M---'
const CHILD_BOUNDARY = '---ANN0T4T10N_CH1LD---'

const createBoundaryRegexp = (boundary) =>
  new RegExp(`\\|${boundary}\\-(.*?)\\-${boundary}\\|`, 'gm')
const CATEGORY_MARK_REGEX = createBoundaryRegexp(CATEGORY_BOUNDARY)
const ITEM_MARK_REGEX = createBoundaryRegexp(ITEM_BOUNDARY)
const CHILD_MARK_REGEX = createBoundaryRegexp(CHILD_BOUNDARY)

const _regexSearch = (regex, text) => {
  regex.lastIndex = 0
  const match = regex.exec(text)
  if (!match) {
    return null
  }
  const [fullMatch, categoryId, itemId, childId, innerText] = match
  const { index } = match
  return {
    fullMatch,
    categoryId,
    itemId,
    childId: childId ?? '',
    innerText,
    index,
  }
}

const _regexGetValue = (regex, text) => {
  regex.lastIndex = 0
  const match = regex.exec(text)
  if (!match) {
    return null
  }
  const [fullMatch, value, innerText] = match
  const { index } = match
  return {
    fullMatch,
    value,
    innerText,
    index,
  }
}

export const replaceAnnotationsMarks = (html) => {
  while (true) {
    const match = _regexSearch(TAG_REGEX, html)
    if (!match) {
      break
    }
    const { fullMatch, categoryId, itemId, childId, innerText, index } = match
    html =
      html.slice(0, index) +
      `|${CATEGORY_BOUNDARY}-${categoryId}-${CATEGORY_BOUNDARY}||${ITEM_BOUNDARY}-${itemId}-${ITEM_BOUNDARY}|${innerText}|${CHILD_BOUNDARY}-${childId}-${CHILD_BOUNDARY}|` +
      html.slice(index + fullMatch.length)
  }
  return html
}

const _applyEntity = (start, end, entityKey) => (characterMetadata, index) => {
  const inRange = index >= start && index < end
  return inRange
    ? CharacterMetadata.applyEntity(characterMetadata, entityKey)
    : characterMetadata
}

const _AnnotationApplyMark = (contentBlock) => {
  if (contentBlock.getType() === 'atomic') {
    return null
  }

  // Category match
  const categoryMatch = _regexGetValue(
    CATEGORY_MARK_REGEX,
    contentBlock.getText()
  )
  if (!categoryMatch) {
    return null
  }
  let updatedBlock = _stripCharacters(
    contentBlock,
    categoryMatch.index,
    categoryMatch.fullMatch.length
  )

  // Item match
  ITEM_MARK_REGEX.lastIndex = 0
  const itemMatch = _regexGetValue(ITEM_MARK_REGEX, updatedBlock.getText())
  if (!itemMatch) {
    return null
  }
  updatedBlock = _stripCharacters(
    updatedBlock,
    itemMatch.index,
    itemMatch.fullMatch.length
  )

  // Child match
  CHILD_MARK_REGEX.lastIndex = 0
  const childMatch = _regexGetValue(CHILD_MARK_REGEX, updatedBlock.getText())
  if (!childMatch) {
    return null
  }
  updatedBlock = _stripCharacters(
    updatedBlock,
    childMatch.index,
    childMatch.fullMatch.length
  )

  const entityKey = Entity.create(ANNOTATION_STYLE, 'MUTABLE', {
    categoryId: categoryMatch.value,
    itemId: itemMatch.value,
    childId: childMatch.value,
  } as AnnotationData)

  const characterList = updatedBlock
    .getCharacterList()
    .map(_applyEntity(categoryMatch.index, childMatch.index, entityKey))
  return updatedBlock.merge({ characterList })
}

const applyUntilNull = (contentBlock) => {
  let prevBlock
  do {
    prevBlock = contentBlock
    contentBlock = _AnnotationApplyMark(prevBlock)
  } while (contentBlock !== null)
  return prevBlock
}

export const annotationApplyMarks = (data) => {
  data.contentBlocks = data.contentBlocks.map(applyUntilNull)
  return data
}
